import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/EditOutlined";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import ChatIcon from "@material-ui/icons/ChatBubbleOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { T, Chip, Dot, SelectField, ACTIVITY, actorText, fmtPhone, fmtField, fmtDate, timeAgo, sourceKind, sourceLabel, useCrmSocket, useDebounced, money } from "./common";

const useStyles = makeStyles(theme => ({
  paper: { width: 460, maxWidth: "100vw", display: "flex", flexDirection: "column", background: "#fff" },
  head: { padding: "16px 18px 12px", borderBottom: `1px solid ${T.line}`, display: "flex", flexDirection: "column", gap: 8 },
  row: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  name: { fontFamily: theme.app.heading, fontSize: 19, fontWeight: 600, color: T.ink, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  phone: { fontFamily: T.mono, fontSize: 12, color: T.ink2 },
  progress: { display: "flex", gap: 3, marginTop: 2 },
  seg: { flex: 1, height: 6, borderRadius: 3, background: T.lineSoft },
  tabs: { display: "flex", gap: 16, padding: "0 18px", borderBottom: `1px solid ${T.line}` },
  tab: { fontSize: 12.5, padding: "9px 0", color: T.ink3, cursor: "pointer", borderBottom: "2px solid transparent", background: "none", border: "none", font: "inherit" },
  tabOn: { color: T.ink, borderBottom: `2px solid ${T.dark}`, fontWeight: 600 },
  body: { flex: 1, overflowY: "auto" },
  fields: { padding: "12px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px" },
  f: { display: "flex", flexDirection: "column", gap: 1, minWidth: 0 },
  fl: { fontSize: 10.5, color: T.ink3, display: "flex", gap: 4, alignItems: "center" },
  fv: { fontSize: 12.5, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", wordBreak: "break-word" },
  auto: { fontSize: 9, color: T.alt, background: T.altSoft, borderRadius: 4, padding: "0 4px" },
  ia: { fontSize: 9, color: T.limeInk, background: T.limeSoft, borderRadius: 4, padding: "0 4px" },
  section: { padding: "8px 18px 4px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.ink3 },
  tl: { padding: "0 18px 16px" },
  ev: { display: "grid", gridTemplateColumns: "20px 1fr", gap: 8, padding: "8px 0", borderTop: `1px solid ${T.lineSoft}` },
  ic: { width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 9, color: "#fff", fontWeight: 700 },
  et: { fontSize: 12, color: T.ink },
  em: { fontSize: 10.5, color: T.ink3 },
  eq: { fontSize: 11.5, color: T.ink2, background: T.lineSoft, borderRadius: 7, padding: "4px 8px", marginTop: 3, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  acts: { display: "flex", gap: 6, padding: "10px 18px", borderTop: `1px solid ${T.line}`, flexWrap: "wrap", alignItems: "center" },
  btn: { textTransform: "none", borderRadius: 8, fontSize: 12, borderColor: T.line, color: T.ink },
  btnDark: { textTransform: "none", borderRadius: 8, fontSize: 12, background: T.dark, color: "#fff", "&:hover": { background: "#1f262c" } },
  note: { padding: "8px 18px", display: "flex", gap: 6, alignItems: "flex-start" },
  card: { margin: "8px 18px", border: `1px solid ${T.line}`, borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 8 },
  editGrid: { padding: "12px 18px", display: "flex", flexDirection: "column", gap: 10 },
  optRow: { display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: T.ink2, padding: "4px 18px" },
  empty: { padding: 24, color: T.ink3, fontSize: 13, textAlign: "center" },
}));

const STAGE_KIND_COLOR = { ganho: T.ok, perdido: T.danger };

// valor de um item da timeline em texto curto (para as atividades com conteúdo)
const activityDetail = a => {
  const p = a.payload || {};
  if (a.type === "mensagem.recebida" || a.type === "campanha.enviada" || a.type === "campanha.respondida") return p.texto || "";
  if (a.type === "nota") return p.texto || "";
  if (a.type === "ligacao") return p.resumo || "";
  if (a.type === "ia.resultado")
    return Object.keys(p.resultado || {})
      .map(k => `${k}: ${typeof p.resultado[k] === "object" ? JSON.stringify(p.resultado[k]) : p.resultado[k]}`)
      .join("\n");
  if (a.type === "campo.alterado")
    return (p.alteracoes || [])
      .slice(0, 6)
      .map(x => `${x.campo}: ${x.de == null || x.de === "" ? "—" : x.de} → ${x.para == null || x.para === "" ? "—" : x.para}`)
      .join("\n");
  if (a.type === "tag") return [...(p.adicionadas || []).map(t => `+${t}`), ...(p.removidas || []).map(t => `-${t}`)].join(" ");
  if (a.type === "etapa.alterada" && p.motivo && p.motivo !== actorText(a.actor)) return p.motivo;
  return "";
};

const ContactDrawer = ({ contactId, open, onClose, onChanged }) => {
  const c = useStyles();
  const history = useHistory();
  const { confirm } = useConfirm();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumo");
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) return;
    try {
      const { data: d } = await api.get(`/crm/contacts/${contactId}`);
      setData(d);
    } catch (err) {
      toastError(err);
    }
  }, [contactId]);

  useEffect(() => {
    setData(null);
    setTab("resumo");
    setEditing(false);
    setNote("");
    if (open && contactId) load();
  }, [open, contactId, load]);

  const reload = useDebounced(load, 350);
  useCrmSocket(p => {
    if (!open || !contactId) return;
    if (p.kind === "reconnect" || Number(p.contactId) === Number(contactId)) reload();
  });

  const contact = data && data.contact;
  const fields = (data && data.fields) || [];
  const cards = (data && data.cards) || [];
  const fieldOf = key => fields.find(f => f.key === key);
  const attrs = (contact && contact.attributes) || {};
  const attrKeys = Object.keys(attrs).filter(k => !(fieldOf(k) || {}).hidden);
  // ordem: campos do dicionário (posição), depois chaves desconhecidas
  attrKeys.sort((a, b) => {
    const fa = fieldOf(a);
    const fb = fieldOf(b);
    return (fa ? fa.position : 999) - (fb ? fb.position : 999) || a.localeCompare(b);
  });

  const changed = () => onChanged && onChanged();

  const moveTo = async (card, stageId) => {
    try {
      await api.post(`/crm/cards/${card.id}/move`, { toStageId: stageId });
      await load();
      changed();
    } catch (err) {
      toastError(err);
    }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/crm/contacts/${contact.id}/notes`, { text: note, cardId: cards[0] ? cards[0].id : null });
      setNote("");
      await load();
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const startEdit = () => {
    setForm({ name: contact.name || "", phone: contact.phone ? fmtPhone(contact.phone) : "", email: contact.email || "", tags: (contact.tags || []).join(", "), attributes: { ...attrs } });
    setEditing(true);
    setTab("campos");
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const attributes = {};
      Object.keys(form.attributes || {}).forEach(k => {
        if (JSON.stringify(form.attributes[k]) !== JSON.stringify(attrs[k])) attributes[k] = form.attributes[k];
      });
      await api.patch(`/crm/contacts/${contact.id}`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        tags: String(form.tags || "")
          .split(",")
          .map(t => t.trim())
          .filter(Boolean),
        attributes,
      });
      setEditing(false);
      toast.success("Contato salvo.");
      await load();
      changed();
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const remove = async () => {
    const ok = await confirm({ title: "Excluir contato?", message: `${contact.name || fmtPhone(contact.phone) || contact.email} sai do CRM com todos os cards e o histórico. Isso não pode ser desfeito.`, confirmText: "Excluir", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/crm/contacts/${contact.id}`);
      changed();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  const toggleOptOut = async channel => {
    try {
      await api.post(`/crm/contacts/${contact.id}/optout`, { channel, value: !(contact.optOut || {})[channel] });
      await load();
    } catch (err) {
      toastError(err);
    }
  };

  const removeCard = async card => {
    const ok = await confirm({ title: "Tirar do funil?", message: `O contato sai do funil "${card.pipelineName}". O contato e o histórico continuam no CRM.`, confirmText: "Tirar do funil", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/crm/cards/${card.id}`);
      await load();
      changed();
    } catch (err) {
      toastError(err);
    }
  };

  const saveCardValue = async (card, value) => {
    try {
      await api.patch(`/crm/cards/${card.id}`, { value: value === "" ? null : Number(String(value).replace(/\./g, "").replace(",", ".")) });
      await load();
      changed();
    } catch (err) {
      toastError(err);
    }
  };

  const conv = data && data.conversation;
  const openConversation = () => history.push(`/agents/${conv.agentId}?tab=conversas&conv=${contact.phone}`);

  const main = cards[0];
  const mainIdx = main ? main.stages.findIndex(s => s.id === main.stageId) : -1;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: c.paper }}>
      {!contact ? (
        <div className={c.empty}>Carregando…</div>
      ) : (
        <>
          <div className={c.head}>
            <div className={c.row}>
              <span className={c.name}>{contact.name || fmtPhone(contact.phone) || contact.email}</span>
              {conv && conv.human ? <Chip kind="human">● Atendente</Chip> : null}
              <Tooltip title="Editar">
                <IconButton size="small" onClick={startEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <div className={c.row}>
              {contact.phone && <span className={c.phone}>{fmtPhone(contact.phone)}</span>}
              {contact.email && <span className={c.phone}>{contact.email}</span>}
            </div>
            <div className={c.row}>
              {(contact.sources || []).slice(0, 3).map((s, i) => (
                <Chip key={i} kind={sourceKind(s)}>
                  {sourceLabel(s)}
                </Chip>
              ))}
              {(contact.tags || []).map(t => (
                <Chip key={t}>#{t}</Chip>
              ))}
              {(contact.optOut || {}).whatsapp && <Chip kind="danger">não recebe WhatsApp</Chip>}
            </div>
            {main && (
              <>
                <div className={c.progress} title={`${main.pipelineName}: etapa ${mainIdx + 1} de ${main.stages.length}`}>
                  {main.stages.map((s, i) => (
                    <span key={s.id} className={c.seg} style={{ background: i < mainIdx ? T.dark : i === mainIdx ? STAGE_KIND_COLOR[s.kind] || T.lime : T.lineSoft, border: i === mainIdx ? `1px solid ${T.dark}` : "none" }} />
                  ))}
                </div>
                <div className={c.row} style={{ fontSize: 11.5, color: T.ink2 }}>
                  <span style={{ flex: 1 }}>
                    {main.pipelineName} · <b>{(main.stages[mainIdx] || {}).name}</b> · {timeAgo(main.enteredStageAt)} nesta etapa
                  </span>
                  <SelectField fullWidth={false} value={main.stageId} onChange={v => moveTo(main, v)} options={main.stages.map(s => ({ value: s.id, label: s.name }))} className={c.btn} />
                </div>
              </>
            )}
          </div>

          <div className={c.tabs}>
            {[
              ["resumo", "Resumo"],
              ["campos", `Campos (${attrKeys.length})`],
              ["funis", `Funis (${cards.length})`],
            ].map(([k, l]) => (
              <button type="button" key={k} className={`${c.tab} ${tab === k ? c.tabOn : ""}`} onClick={() => setTab(k)}>
                {l}
              </button>
            ))}
          </div>

          <div className={c.body}>
            {tab === "resumo" && (
              <>
                {attrKeys.length > 0 && (
                  <div className={c.fields}>
                    {attrKeys.slice(0, 8).map(k => {
                      const f = fieldOf(k);
                      return (
                        <div key={k} className={c.f}>
                          <span className={c.fl}>
                            {(f && f.label) || k}
                            {k.startsWith("ia_") ? <span className={c.ia}>IA</span> : f && f.origin === "auto" ? <span className={c.auto}>auto</span> : null}
                          </span>
                          <span className={c.fv}>{fmtField(f && f.type, attrs[k])}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className={c.note}>
                  <TextField value={note} onChange={e => setNote(e.target.value)} placeholder="Escrever uma nota…" variant="outlined" size="small" fullWidth multiline maxRows={4} />
                  <Button variant="outlined" className={c.btn} disabled={busy || !note.trim()} onClick={saveNote}>
                    Salvar
                  </Button>
                </div>
                <div className={c.section}>Histórico</div>
                <div className={c.tl}>
                  {(data.activities || []).map(a => {
                    const meta = ACTIVITY[a.type] || { ic: "•", color: T.ink3, title: () => a.type };
                    const detail = activityDetail(a);
                    return (
                      <div key={a.id} className={c.ev}>
                        <span className={c.ic} style={{ background: meta.color }}>
                          {meta.ic}
                        </span>
                        <div>
                          <div className={c.et}>{meta.title(a)}</div>
                          {detail ? <div className={c.eq}>{detail}</div> : null}
                          <div className={c.em} title={fmtDate(a.createdAt)}>
                            {[actorText(a.actor), timeAgo(a.createdAt)].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {!(data.activities || []).length && <div className={c.empty}>Nada por aqui ainda.</div>}
                </div>
              </>
            )}

            {tab === "campos" &&
              (editing ? (
                <div className={c.editGrid}>
                  <TextField label="Nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                  <TextField label="Telefone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                  <TextField label="E-mail" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                  <TextField label="Tags (vírgula)" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                  {Object.keys(form.attributes || {}).map(k => {
                    const f = fieldOf(k);
                    return (
                      <TextField
                        key={k}
                        label={(f && f.label) || k}
                        value={form.attributes[k] == null ? "" : typeof form.attributes[k] === "object" ? JSON.stringify(form.attributes[k]) : form.attributes[k]}
                        onChange={e => setForm({ ...form, attributes: { ...form.attributes, [k]: e.target.value } })}
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    );
                  })}
                  <div className={c.row}>
                    <Button variant="outlined" className={c.btn} onClick={() => setEditing(false)}>
                      Cancelar
                    </Button>
                    <Button className={c.btnDark} disabled={busy} onClick={saveEdit}>
                      Salvar contato
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={c.fields}>
                    {attrKeys.map(k => {
                      const f = fieldOf(k);
                      return (
                        <div key={k} className={c.f}>
                          <span className={c.fl}>
                            {(f && f.label) || k}
                            {k.startsWith("ia_") ? <span className={c.ia}>IA</span> : f && f.origin === "auto" ? <span className={c.auto}>auto</span> : null}
                          </span>
                          <span className={c.fv}>{fmtField(f && f.type, attrs[k])}</span>
                        </div>
                      );
                    })}
                    {!attrKeys.length && <div className={c.em}>Sem campos extras.</div>}
                  </div>
                  <div className={c.section}>Mensagens</div>
                  {[
                    ["whatsapp", "WhatsApp"],
                    ["sms", "SMS"],
                    ["call", "Ligações"],
                  ].map(([k, l]) => (
                    <div key={k} className={c.optRow}>
                      <span style={{ flex: 1 }}>
                        {l}: {(contact.optOut || {})[k] ? <b style={{ color: T.danger }}>não recebe campanhas</b> : "recebe campanhas"}
                      </span>
                      <Button size="small" variant="outlined" className={c.btn} onClick={() => toggleOptOut(k)}>
                        {(contact.optOut || {})[k] ? "Liberar" : "Bloquear"}
                      </Button>
                    </div>
                  ))}
                  <div className={c.optRow}>
                    <span style={{ flex: 1 }}>Criado em {fmtDate(contact.createdAt)}</span>
                  </div>
                </>
              ))}

            {tab === "funis" && (
              <>
                {cards.map(card => (
                  <div key={card.id} className={c.card}>
                    <div className={c.row}>
                      <b style={{ fontSize: 13, flex: 1 }}>{card.pipelineName}</b>
                      {card.closedAt && <Chip kind={(card.stages.find(s => s.id === card.stageId) || {}).kind === "ganho" ? "ok" : "danger"}>encerrado</Chip>}
                      <IconButton size="small" onClick={() => removeCard(card)} title="Tirar do funil">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <SelectField label="Etapa" value={card.stageId} onChange={v => moveTo(card, v)} options={card.stages.map(s => ({ value: s.id, label: s.name }))} />
                    <TextField
                      label="Valor do card (R$)"
                      defaultValue={card.value != null ? String(card.value).replace(".", ",") : ""}
                      onBlur={e => {
                        const v = e.target.value.trim();
                        const cur = card.value != null ? String(card.value).replace(".", ",") : "";
                        if (v !== cur) saveCardValue(card, v);
                      }}
                      helperText={card.value == null ? "Vazio = usa o campo de valor do contato" : money(card.value, true)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <div className={c.em}>
                      <Dot color={(card.stages.find(s => s.id === card.stageId) || {}).color} /> nesta etapa {timeAgo(card.enteredStageAt)}
                    </div>
                  </div>
                ))}
                {!cards.length && <div className={c.empty}>Este contato ainda não está em nenhum funil.</div>}
              </>
            )}
          </div>

          <div className={c.acts}>
            {conv ? (
              <Button variant="outlined" className={c.btn} startIcon={<ChatIcon fontSize="small" />} onClick={openConversation}>
                Abrir conversa
              </Button>
            ) : (
              <span className={c.em}>Sem conversa de WhatsApp com agente ainda.</span>
            )}
            <span style={{ flex: 1 }} />
            <Button variant="outlined" className={c.btn} style={{ color: T.danger }} onClick={remove}>
              Excluir
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default ContactDrawer;
